import { Component, inject, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService } from './services/auth.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrModule } from 'ngx-toastr';
import { PosthogService } from './services/posthog.service';
import { AppearanceService } from './services/appearance.service';
import { filter } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterModule, ToastrModule, NgxSpinnerModule, MatTooltipModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  title = 'F&F Dashboard';

  /**
   * ngx-spinner takes its colour as an input rather than from CSS, so the
   * brand token has to be resolved here. Read lazily: the appearance
   * service applies the theme in this component's constructor, and the
   * user can change it without a reload.
   */
  get spinnerColor(): string {
    return getComputedStyle(document.body).getPropertyValue('--c-brand').trim() || '#b31d2c';
  }

  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  authService = inject(AuthService);
  activeTab: string = 'Menus';

  private appearanceService = inject(AppearanceService);
  private router = inject(Router);
  private spinner = inject(NgxSpinnerService);

  constructor(private posthogService: PosthogService) {
    this.appearanceService.init();
    this.matIconRegistry.addSvgIcon(
      'dashboard-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/dashboard.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'receipt-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/order.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'reservation-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/sidebar/table.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'menu-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/menu.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'promotion-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/tag.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'logout-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/logout.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'discounts-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/discount.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'table-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/table.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'shift-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/user-group.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'permission-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/office-building.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'analytics-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/dollar.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'support-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/customer-support.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'settings-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/setting.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'search-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/magnifier.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'notification-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/bell.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'dark-mode-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/sidebar/dark-mode.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'warning-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/common/warning.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'staff-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/common/user-circle.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'dish-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/common/dish.svg'),
    );
  }

  ngOnInit(): void {
    // The spinner's backdrop covers the whole app, so one request that ends on
    // a path without a matching hide() leaves a veil over every later screen.
    // A spinner should never outlive the page that raised it.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.spinner.hide());
  }

  changeInCurrentTab(activeTab: string) {
    this.activeTab = activeTab;
  }
}
